import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

export interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  /** Marks the few fields that may be left blank; required is the norm and stays unlabelled. */
  optional?: boolean;
  className?: string;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': true | undefined;
    'aria-required': true | undefined;
  }) => ReactNode;
}

/** Label, control, hint and inline error wired together for assistive technology. */
export function Field({ label, hint, error, required, optional, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className={['flex flex-col gap-1.5', className].filter(Boolean).join(' ')}>
      <label htmlFor={id} className="text-body font-medium text-ink">
        {label}
        {optional ? <span className="text-ink-3 font-normal"> (optional)</span> : null}
      </label>
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required ? true : undefined,
      })}
      {hint ? (
        <p id={hintId} className="text-meta text-ink-3">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-meta text-danger-ink font-medium" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/* Controls are 32px tall, like an md button, so a field and its button line up. */
const CONTROL =
  'w-full rounded-sm border border-line-strong bg-surface text-ink text-body placeholder:text-ink-3 transition-tone ' +
  'hover:border-ink-3 aria-invalid:border-danger disabled:opacity-55 disabled:cursor-not-allowed';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={[CONTROL, 'h-8 px-3', className].filter(Boolean).join(' ')}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={[CONTROL, 'px-3 py-1.5', className].filter(Boolean).join(' ')}
      {...props}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={[CONTROL, 'h-8 px-2.5 pr-8', className].filter(Boolean).join(' ')}
        {...props}
      >
        {children}
      </select>
    );
  },
);

export function Checkbox({
  label,
  hint,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode }) {
  const id = useId();
  return (
    <div className={['flex items-start gap-2.5', className].filter(Boolean).join(' ')}>
      <input id={id} type="checkbox" className="mt-0.5 size-4 accent-(--brand)" {...props} />
      <label htmlFor={id} className="text-body text-ink">
        {label}
        {hint ? <span className="block text-meta text-ink-3">{hint}</span> : null}
      </label>
    </div>
  );
}

/** Options as rows: the chosen one is filled, the others stay flat until hovered. */
export function RadioGroup<T extends string>({
  legend,
  name,
  value,
  onChange,
  options,
}: {
  legend: string;
  name: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; hint?: ReactNode; disabled?: boolean }[];
}) {
  return (
    <fieldset className="m-0 p-0 border-0 min-w-0">
      <legend className="text-body font-medium text-ink mb-2">{legend}</legend>
      <div className="flex flex-col gap-1">
        {options.map((option) => {
          const id = `${name}-${option.value}`;
          const checked = option.value === value;
          return (
            <label
              key={option.value}
              htmlFor={id}
              className={[
                'flex items-start gap-2.5 rounded-sm border px-3 py-2 cursor-pointer transition-tone',
                checked ? 'border-brand/40 bg-brand-soft' : 'border-transparent hover:bg-surface-2',
                option.disabled ? 'opacity-55 cursor-not-allowed' : '',
              ].join(' ')}
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                disabled={option.disabled}
                onChange={() => onChange(option.value)}
                className="mt-0.5 size-4 accent-(--brand)"
              />
              <span className="text-body">
                {option.label}
                {option.hint ? (
                  <span className="block text-meta text-ink-3">{option.hint}</span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
